import type { Udalost } from '../../api/JsdhNotifikaceService/generated/models/Udalost';
import type { Odpoved } from '../../api/JsdhNotifikaceService/generated/models/Odpoved';
import type { Technika } from '../../api/JsdhNotifikaceService/generated/models/Technika';
import type { PoplachTechnikaResponse } from '../../api/JsdhNotifikaceService/generated/models/PoplachTechnika';
import type { PosilovaNotifikaceLogResponse } from '../../api/JsdhNotifikaceService/generated/models/PosilovaNotifikaceLog';
import type { UdalostRouteResponse } from '../../api/JsdhNotifikaceService/generated/models/UdalostRoute';
import type { JednotkaDto } from '../../api/JsdhNotifikaceService/generated/models/JednotkaDto';
import { PRESETS, type PoplachConfig } from '../../api/JsdhNotifikaceService/generated/models/DeviceToken';

export type MockVariant = 'full' | 'growing' | 'empty' | 'long-text';
export const MOCK_VARIANTS: MockVariant[] = ['full', 'growing', 'empty', 'long-text'];

export interface PoplachMockData {
    aktivniPoplach: Udalost | undefined;
    odpovedi: Odpoved[];
    allTechnika: Technika[];
    technikaSources: PoplachTechnikaResponse[];
    notifikaceLog: PosilovaNotifikaceLogResponse[];
    routeData: UdalostRouteResponse | null;
    jednotka: JednotkaDto;
    poplachConfig: PoplachConfig;
    predpoplach: undefined;
}

const baseJednotka: JednotkaDto = {
    id: 1,
    nazev: 'JSDH Těškovice',
    casVyjezdMinuty: 2,
    adresaLat: 49.7833,
    adresaLng: 18.0167,
} as JednotkaDto;

const basePoplachConfig: PoplachConfig = {
    ...PRESETS['vyjezd-2026'],
};

const baseUdalost = (sessionStartMs: number, overrides: Partial<Udalost> = {}): Udalost => ({
    id: -1,
    pred: 'POŽÁR',
    udalost: 'Nízké budovy',
    objekt: 'Rodinný dům',
    popis: 'Hlášený požár v 1. patře, kouř z oken.',
    upresneni: 'Nikdo není zraněn, sousedé volali.',
    misto: 'Otrokovice', ulice: 'Hálkova',
    cisloPopisne: '149',
    obec: 'Otrokovice',
    gps: '49.2106,17.5293',
    casVyhlaseni: new Date(sessionStartMs).toISOString(),
    technikaJednotky: ['JSDH Těškovice: CAS 25 LIAZ - HOP 778', 'JSDH Těškovice: CAS 20 T815 - HOP 772'],
    technikaIds: [-101, -102],
    dalsiTechnikaJednotek: [],  // naplní getPoplachMockData podle overrides.posily (default DEFAULT_FULL_POSILY)
    posilovaTechnikaTexty: [],
    token: 'PREVIEW',
    ...overrides,
} as Udalost);

const baseTechnika = (id: number, volaciZnak: string, nazev: string, spz?: string): Technika => ({
    id,
    volaciZnak,
    nazev,
    spz,
    aktivni: true,
    maFotografii: false,
});

const TECHNIKA_POOL: Technika[] = [
    baseTechnika(-101, 'HOP 778', 'CAS 25 LIAZ', '1A2 3456'),
    baseTechnika(-102, 'HOP 772', 'CAS 20 T815 Terno', '2B3 4567'),
    baseTechnika(-103, 'HOP 765', 'AZ 30 Iveco', '3C4 5678'),
    baseTechnika(-104, 'HOP 754', 'DA 12 Mercedes', '4D5 6789'),
    baseTechnika(-105, 'HOP 743', 'TA 10 Renault', '5E6 7890'),
    baseTechnika(-106, 'HOP 731', 'PHA 32 MAN', '6F7 8901'),
];

// Poznámky velitele na technice — shodné s textem POSILOVA_TECHNIKA zprávy v notifikaci
// (viz buildFullNotifikace), aby v UI bylo vidět, že stejný text se zobrazuje na kartě i ve Zprávách.
const POSILA_TEXT_DEFAULT = 'Druhé CAS na adresu, oheň se šíří na podkroví.';

const SOURCES_POOL: PoplachTechnikaResponse[] = [
    { technikaId: -101, volaciZnak: 'HOP 778', nazev: 'CAS 25 LIAZ', zdroj: 'KOPIS', potvrzenoKopis: false },
    { technikaId: -102, volaciZnak: 'HOP 772', nazev: 'CAS 20 T815', zdroj: 'VELITEL', potvrzenoKopis: true, poznamka: POSILA_TEXT_DEFAULT },
    { technikaId: -103, volaciZnak: 'HOP 765', nazev: 'AZ 30 Iveco', zdroj: 'KOPIS', potvrzenoKopis: false },
    { technikaId: -104, volaciZnak: 'HOP 754', nazev: 'DA 12 Mercedes', zdroj: 'VELITEL', potvrzenoKopis: false, poznamka: POSILA_TEXT_DEFAULT },
    { technikaId: -105, volaciZnak: 'HOP 743', nazev: 'TA 10 Renault', zdroj: 'KOPIS', potvrzenoKopis: false },
    { technikaId: -106, volaciZnak: 'HOP 731', nazev: 'PHA 32 MAN', zdroj: 'VELITEL', potvrzenoKopis: true, poznamka: POSILA_TEXT_DEFAULT },
] as PoplachTechnikaResponse[];

const DEFAULT_FULL_TECHNIKA_COUNT = 2;

// Pool dalších jednotek (technika dalších jednotek) — kolik se zobrazí řídí overrides.posily.
const POSILY_POOL: string[] = [
    'Bílovec: CAS 20 MAN - HOP 511',
    'Studénka: CAS 30 Tatra - HOP 524',
    'Klimkovice: CAS 24 Scania - HOP 537',
    'Ostrava-Poruba: AZ 30 Iveco - HOP 605',
    'Opava: CAS 20 MAN - HOP 412',
    'Hlučín: CAS 24 Volvo - HOP 388',
    'Frýdek-Místek: CAS 20 MAN - HOP 290',
    'Havířov: AZ 30 Iveco - HOP 215',
    'Karviná: CAS 24 Tatra - HOP 178',
    'Český Těšín: DA 12 Mercedes - HOP 156',
    'Třinec: CAS 20 Scania - HOP 134',
    'Bohumín: TA 10 Renault - HOP 098',
];
const DEFAULT_FULL_POSILY = 3;

const baseFunkce = (kod: string) => ({ id: 1, kod, nazev: kod } as any);

const JEDU_NAMES = [
    'Petr Novák', 'Tomáš Brada', 'Lukáš Bureš', 'Jiří Píšek', 'Marek Šiška',
    'Pavel Horák', 'Jakub Doležal', 'David Černý', 'Ondřej Veselý', 'Jan Procházka',
    'Roman Beneš', 'Vojtěch Marek', 'Adam Pokorný', 'Filip Kučera', 'Lubomír Krejčí',
    'Štěpán Mareš', 'Václav Holub', 'Aleš Kovář', 'Radek Sedlák', 'Daniel Bárta',
];

const NEJEDU_NAMES = [
    'Michal Gregor', 'Karel Dvořák', 'Petr Škopík', 'Zdeněk Hradil', 'Miroslav Mikeš',
    'Jaroslav Žaludek', 'Martin Jurek', 'Miroslav Baroň', 'Robert Špaček', 'Tomáš Vlk',
    'Igor Vrána', 'Vlastimil Polák', 'Patrik Fiala', 'Stanislav Kadlec', 'Bohuslav Toman',
    'Ladislav Dvořáček', 'Zbyněk Šimek', 'Jindřich Růžička', 'Karel Slezák', 'Antonín Müller',
];

// Mix funkčních kombinací: single-role (VD/S/H), dual-role (VD+S, VD+H, S+H), triple (VD+S+H).
// Slouží k vizuálnímu testu počítadel (greedy assignment) a chip rendrování v Vyjezd2026OdpovediSection.
const FUNKCE_PATTERN: string[][] = [
    ['VD'], ['S'], ['H'], ['VD', 'S'], ['VD', 'S', 'H'],
    ['VD', 'H'], ['S', 'H'], ['H'], ['VD'], ['S'],
    ['VD', 'S'], ['VD', 'S', 'H'], ['H'], ['VD', 'H'], ['S', 'H'],
    ['VD'], ['S'], ['H'], ['VD', 'S'], ['VD', 'S', 'H'],
];

// Periodický pattern napříč JEDU i NEJEDU — chceme různé kombinace specializací
// pro vizuální test (1 spec, 2 spec, žádné, +X overflow se 3 spec).
const SPECIALIZACE_PATTERN: string[][] = [
    ['NDT'], [], ['PILAR'], ['NDT', 'PILAR'], [],
    ['NDT'], ['PILAR'], [], ['NDT', 'PILAR', 'OBSL_MOTORPILY'], ['NDT'],
    [], ['PILAR'], ['NDT'], [], ['NDT', 'PILAR'],
    ['PILAR'], [], ['NDT'], [], ['NDT', 'PILAR'],
];

const baseSpecializace = (kod: string) => ({ id: 1, kod, nazev: kod } as any);

const buildOdpovedi = (jeduCount: number, nejeduCount: number): Odpoved[] => {
    const result: Odpoved[] = [];
    const jeduMax = Math.min(Math.max(0, jeduCount), JEDU_NAMES.length);
    const nejeduMax = Math.min(Math.max(0, nejeduCount), NEJEDU_NAMES.length);
    for (let i = 0; i < jeduMax; i++) {
        result.push({
            id: -(i + 1),
            poplachId: -1,
            uzivatelId: i + 1,
            jmeno: JEDU_NAMES[i],
            odpoved: 'JEDU',
            cas: new Date().toISOString(),
            funkce: FUNKCE_PATTERN[i].map(baseFunkce),
            specializace: SPECIALIZACE_PATTERN[i].map(baseSpecializace),
        } as Odpoved);
    }
    for (let i = 0; i < nejeduMax; i++) {
        result.push({
            id: -(jeduMax + i + 1),
            poplachId: -1,
            uzivatelId: jeduMax + i + 1,
            jmeno: NEJEDU_NAMES[i],
            odpoved: 'NEJEDU',
            cas: new Date().toISOString(),
            specializace: SPECIALIZACE_PATTERN[(i + 2) % SPECIALIZACE_PATTERN.length].map(baseSpecializace),
        } as Odpoved);
    }
    return result;
};

const DEFAULT_FULL_JEDU = 5;
const DEFAULT_FULL_NEJEDU = 7;

const buildFullNotifikace = (sessionStartMs: number): PosilovaNotifikaceLogResponse[] => [
    { id: -1, text: 'Pošlete pěnidlo, dochází nám zásoba.', recipients: 'ALL', pocetOdeslanych: 8, kriticka: false, odeslalJmeno: 'Petr Novák', createdAt: new Date(sessionStartMs + 30 * 1000).toISOString(), typ: 'ZPRAVA' },
    { id: -2, text: 'Druhé CAS na adresu, oheň se šíří na podkroví.', recipients: 'ALL', pocetOdeslanych: 25, kriticka: true, odeslalJmeno: 'Petr Novák', createdAt: new Date(sessionStartMs + 60 * 1000).toISOString(), typ: 'POSILOVA_TECHNIKA' },
];

const buildLongTextNotifikace = (sessionStartMs: number): PosilovaNotifikaceLogResponse[] => [
    { id: -1, text: 'Velmi dlouhý text zprávy, který má za úkol otestovat ellipsis a clamp na 3 řádky. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.', recipients: 'ALL', pocetOdeslanych: 8, kriticka: false, odeslalJmeno: 'Petr Novák Velmi Dlouhé Příjmení', createdAt: new Date(sessionStartMs + 30 * 1000).toISOString(), typ: 'ZPRAVA' },
    { id: -2, text: 'Druhá dlouhá zpráva s požadavkem na posilu — pošlete CAS na adresu Hálkova 149, vchod od jihu, oheň se šíří, evakuace probíhá, čekejte na velitele zásahu.', recipients: 'ALL', pocetOdeslanych: 25, kriticka: true, odeslalJmeno: 'Petr Novák', createdAt: new Date(sessionStartMs + 60 * 1000).toISOString(), typ: 'POSILOVA_TECHNIKA' },
];

export interface PoplachMockOverrides {
    technika?: number;  // počet karet techniky (0–TECHNIKA_POOL.length)
    jedu?: number;      // počet odpovědí JEDU (0–JEDU_NAMES.length)
    nejedu?: number;    // počet odpovědí NEJEDU (0–NEJEDU_NAMES.length)
    posily?: number;    // počet "Technika dalších jednotek" (0–POSILY_POOL.length)
    mapaLayout?: 'misto' | 'trasa' | 'misto-trasa' | 'off';  // přepíše layout mapy
    mapMode?: 'auto' | 'fixed' | 'cycle';  // režim zoomu mapy
    fixedZoom?: number;  // zoom level pro mode='fixed'
    zoom1?: number;      // zoom level 1 pro mode='cycle'
    zoom2?: number;      // zoom level 2 pro mode='cycle'
    interval?: number;   // interval přepínání pro mode='cycle' (ms)
    pred?: string;       // typ události (např. POŽÁR, DOPRAVNÍ NEHODA)
    udalost?: string;    // podtyp události (např. Nízké budovy, Osobní auto)
    popis?: string;      // vlastní text popisu
    upresneni?: string;  // vlastní text upřesnění
    nocniRezim?: 'auto' | 'light' | 'dark';  // přepíše noční mód v presetu
    pravidla?: boolean;  // přepíše pravidlaVyjezdu.enabled v presetu (zapne/vypne plombu)
}

// Běžné typy událostí — použité v admin selectu pro mock náhled.
export const MOCK_PRED_TYPES = [
    'POŽÁR',
    'DOPRAVNÍ NEHODA',
    'TECHNICKÁ POMOC',
    'ŽIVELNÍ POHROMA',
    'ÚNIK NEBEZPEČNÉ LÁTKY',
    'OSTATNÍ',
];

// Mock trasa: jednoduchá polyline ze základny (49.7833, 18.0167) na místo (49.2106, 17.5293).
// 7 mezilehlých bodů aby trasa nebyla přímka.
const MOCK_ROUTE: UdalostRouteResponse = {
    polyline: [
        [49.7833, 18.0167],
        [49.7200, 18.0500],
        [49.6500, 18.0800],
        [49.5500, 17.9800],
        [49.4500, 17.8500],
        [49.3500, 17.7000],
        [49.2800, 17.6200],
        [49.2400, 17.5700],
        [49.2106, 17.5293],
    ],
    distanceKm: 78,
    durationMin: 65,
};

const clampInt = (val: number | undefined, fallback: number, max: number): number => {
    if (val === undefined || Number.isNaN(val)) return fallback;
    return Math.min(Math.max(0, Math.floor(val)), max);
};

export const getPoplachMockData = (
    variant: MockVariant,
    elapsedMs: number,
    sessionStartMs: number = Date.now() - elapsedMs,
    overrides: PoplachMockOverrides = {},
): PoplachMockData => {
    const fullNotifikace = buildFullNotifikace(sessionStartMs);
    const longTextNotifikace = buildLongTextNotifikace(sessionStartMs);

    const techCount = clampInt(overrides.technika, DEFAULT_FULL_TECHNIKA_COUNT, TECHNIKA_POOL.length);
    const jeduCount = clampInt(overrides.jedu, DEFAULT_FULL_JEDU, JEDU_NAMES.length);
    const nejeduCount = clampInt(overrides.nejedu, DEFAULT_FULL_NEJEDU, NEJEDU_NAMES.length);
    const posilyCount = clampInt(overrides.posily, DEFAULT_FULL_POSILY, POSILY_POOL.length);

    const allTechnika = TECHNIKA_POOL.slice(0, techCount);
    const technikaIds = allTechnika.map(t => t.id);
    const technikaSources = SOURCES_POOL.filter(s => technikaIds.includes(s.technikaId));
    // Formát v DB / emailu: "<nazev> - <volaciZnak>" bez prefixu jednotky.
    // Příklad reálného textu z udalost.technikaJednotky:
    //   "CAS 32/8200/800-S3R T815 - HPE 542"
    // Pro mock generujeme stejnou strukturu z TECHNIKA_POOL + 1 fixní unmatched záznam HOP 999,
    // který v poolu chybí — slouží k vizuální verifikaci fallback karty.
    const technikaJednotky = [
        ...allTechnika.map(t => `${t.nazev} - ${t.volaciZnak}`),
        'PHA 32 MAN - HOP 999',
    ];
    const dalsiTechnikaJednotek = POSILY_POOL.slice(0, posilyCount);

    const fullOdpovedi = buildOdpovedi(jeduCount, nejeduCount);

    // mapaLayout: pokud je v overrides, přepíše presetový default.
    // Pro 'trasa' a 'misto-trasa' poskytneme mock polyline (jinak by Vyjezd2026MapaSection
    // padla zpět na 'misto' kvůli !hasRoute).
    const mapaLayout = overrides.mapaLayout ?? basePoplachConfig.mapaLayout ?? 'misto';
    const needsRoute = mapaLayout === 'trasa' || mapaLayout === 'misto-trasa';
    const nocniRezim = overrides.nocniRezim ?? basePoplachConfig.nocniRezim ?? 'auto';
    // Pravidla výjezdu — pokud override.pravidla je true/false, přepíše .enabled.
    // Defaulty (minPocet, vyzadovatVelitele, vyzadovatStrojnika) se zachovají z presetu.
    const basePravidla = basePoplachConfig.pravidlaVyjezdu ?? { enabled: false, minPocet: 4, vyzadovatVelitele: true, vyzadovatStrojnika: true };
    const pravidlaVyjezdu = overrides.pravidla === undefined
        ? basePravidla
        : { ...basePravidla, enabled: overrides.pravidla };
    // Mapa zoom konfigurace — overrides přepíšou jen ta pole, která jsou explicitně poslaná.
    // Pro mode='cycle' bez explicitního intervalu použij default 3500 ms (jako v PoplachConfigForm.handleMapModeChange).
    const baseMapa = basePoplachConfig.mapa ?? { mode: 'auto' };
    const mapaConfig = (overrides.mapMode || overrides.fixedZoom !== undefined
        || overrides.zoom1 !== undefined || overrides.zoom2 !== undefined
        || overrides.interval !== undefined)
        ? {
            ...baseMapa,
            ...(overrides.mapMode && { mode: overrides.mapMode }),
            ...(overrides.fixedZoom !== undefined && { fixedZoom: overrides.fixedZoom }),
            ...(overrides.zoom1 !== undefined && { zoom1: overrides.zoom1 }),
            ...(overrides.zoom2 !== undefined && { zoom2: overrides.zoom2 }),
            ...(overrides.interval !== undefined && { interval: overrides.interval }),
        }
        : baseMapa;
    const poplachConfig: PoplachConfig = { ...basePoplachConfig, mapa: mapaConfig, mapaLayout, nocniRezim, pravidlaVyjezdu };

    // Vlastní text/typ z overrides — prázdný string = nepoužít (zachovat výchozí z baseUdalost).
    const customFields: Partial<Udalost> = {};
    if (overrides.pred?.trim()) customFields.pred = overrides.pred.trim();
    if (overrides.udalost?.trim()) customFields.udalost = overrides.udalost.trim();
    if (overrides.popis?.trim()) customFields.popis = overrides.popis.trim();
    if (overrides.upresneni?.trim()) customFields.upresneni = overrides.upresneni.trim();

    const baseEvent = (more: Partial<Udalost> = {}) =>
        baseUdalost(sessionStartMs, { technikaIds, technikaJednotky, dalsiTechnikaJednotek, ...customFields, ...more });

    const base: PoplachMockData = {
        aktivniPoplach: baseEvent(),
        odpovedi: [],
        allTechnika,
        technikaSources,
        notifikaceLog: [],
        routeData: needsRoute ? MOCK_ROUTE : null,
        jednotka: baseJednotka,
        poplachConfig,
        predpoplach: undefined,
    };

    switch (variant) {
        case 'full':
            return { ...base, odpovedi: fullOdpovedi, notifikaceLog: fullNotifikace };

        case 'empty':
            return base;

        case 'long-text':
            return {
                ...base,
                aktivniPoplach: baseEvent({
                    popis: 'Velmi dlouhý popis události, který má za úkol otestovat clamp na 3 řádky a ellipsis. ' +
                           'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ' +
                           'ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco.',
                    misto: 'Otrokovice-Kvitkovice', ulice: 'Velmi dlouhý název ulice s vícero slovy a interpunkcí',
                    obec: 'Otrokovice-Kvitkovice',
                }),
                odpovedi: fullOdpovedi,
                notifikaceLog: longTextNotifikace,
            };

        case 'growing': {
            const totalCount = jeduCount + nejeduCount;
            const stages = [
                { afterMs: 5000, ratio: 0.1 },
                { afterMs: 10000, ratio: 0.2 },
                { afterMs: 15000, ratio: 0.3 },
                { afterMs: 20000, ratio: 0.45 },
                { afterMs: 30000, ratio: 0.7 },
                { afterMs: 45000, ratio: 1 },
            ];
            const ratio = stages.reduce((acc, s) => elapsedMs >= s.afterMs ? s.ratio : acc, 0);
            const visibleCount = Math.min(totalCount, Math.ceil(totalCount * ratio));
            const odpovedi = fullOdpovedi.slice(0, visibleCount);

            const notif: PosilovaNotifikaceLogResponse[] = [];
            if (elapsedMs >= 30000) notif.push(fullNotifikace[0]);
            if (elapsedMs >= 60000) notif.push(fullNotifikace[1]);

            const aktivniPoplach = elapsedMs >= 25000
                ? baseEvent({ posilovaTechnikaTexty: ['Bílovec CAS 20'] })
                : baseEvent();

            return { ...base, aktivniPoplach, odpovedi, notifikaceLog: notif };
        }
    }
};
